import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Factory, FileText, Handshake, Link2, Percent, Plus, QrCode, ReceiptText, Search, Send, ShoppingCart, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Loading, PageHeader, Surface } from "@/components/ds";
import { NewOrderModal } from "@/components/orders/NewOrderModal";
import { PixChargeModal } from "@/components/orders/PixChargeModal";
import { EditCustomizationModal } from "@/components/orders/EditCustomizationModal";
import { DiscountModal } from "@/components/orders/DiscountModal";
import { Input } from "@/components/ui/input";

const STATUS = {
  PENDENTE: { label: "Recebido", cls: "border-white/15 bg-secondary/60 text-muted-foreground" },
  PAGO: { label: "Pagamento confirmado", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
  EM_PRODUCAO: { label: "Em produção", cls: "border-brand/40 bg-brand/15 text-brand" },
  PRONTO: { label: "Pronto", cls: "border-sky-500/40 bg-sky-500/15 text-sky-300" },
  ENTREGUE: { label: "Entregue", cls: "border-white/15 bg-secondary/60 text-muted-foreground" },
  CANCELADO: { label: "Cancelado", cls: "border-destructive/40 bg-destructive/15 text-red-300" },
};

// Produção é o andamento do pedido. Pagamento é estado separado.
const PRODUCTION_STATUS = ["PENDENTE", "EM_PRODUCAO", "PRONTO", "ENTREGUE", "CANCELADO"];

const LINE_STATE = {
  PRONTO: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  ESTOQUE: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  PRODUZIDO: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  A_PRODUZIR: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  EM_PRODUCAO: "border-brand/40 bg-brand/10 text-brand",
};

const TONE = {
  ok: "text-emerald-300",
  info: "text-brand",
  warn: "text-amber-300",
};

// Situação do pedido em uma frase. Lê só o que o backend já decide (status, plan, payment_status):
// pagamento e produção seguem independentes, a mensagem apenas informa.
const situation = (o) => {
  if (o.status === "CANCELADO") return null;
  if (o.origin === "REPOSICAO_PARCEIRO")
    return o.stock_applied
      ? { tone: "ok", text: "Enviado ao parceiro e creditado no estoque consignado." }
      : null;
  const pago = o.payment_status === "CONFIRMADO";
  if (o.status === "ENTREGUE")
    return pago
      ? { tone: "ok", text: "Pedido entregue e pagamento confirmado. Parabéns! Você finalizou mais um pedido." }
      : { tone: "warn", text: "Pedido entregue. O pagamento ainda está pendente." };
  if (o.plan?.in_production || o.status === "EM_PRODUCAO")
    return { tone: "info", text: "Seu pedido está em produção. Confira os detalhes e o andamento na aba Produção." };
  if (o.plan?.needs_production)
    return { tone: "warn", text: "Pedido recebido. O produto não está disponível em estoque. Próximo passo: enviar para produção." };
  if (o.status === "PRONTO")
    return pago
      ? { tone: "ok", text: "Pedido pronto para entrega. Pagamento confirmado. Próximo passo: entregar ao cliente." }
      : { tone: "warn", text: "Pedido pronto para entrega. Pagamento ainda pendente. Você pode entregar e registrar o pagamento posteriormente." };
  return { tone: "ok", text: "Pedido recebido e produto disponível em estoque. Não é necessário produzir. Próximo passo: preparar para entrega." };
};

const dataHora = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const TABS = [
  ["A_FAZER", "A fazer", ["PENDENTE", "PAGO"]],
  ["EM_PRODUCAO", "Em produção", ["EM_PRODUCAO"]],
  ["PRONTOS", "Prontos", ["PRONTO"]],
  ["CONCLUIDOS", "Concluídos", ["ENTREGUE"]],
  ["CANCELADOS", "Cancelados", ["CANCELADO"]],
  ["TODOS", "Todos", null],
];

const norm = (v) =>
  (v || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const matchesSearch = (o, q) =>
  [o.customer_name, o.phone, o.number, o.partner_name,
   ...(o.items || []).flatMap((i) => [i.name, i.sku, i.variation_name])]
    .some((v) => norm(v).includes(q));

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [managing, setManaging] = useState(null);
  const [prods, setProds] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [tab, setTab] = useState("A_FAZER");
  const [kind, setKind] = useState("TODOS");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [orderCustomer, setOrderCustomer] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [created, setCreated] = useState(null);
  const [pixFor, setPixFor] = useState(null);
  const [editingCustom, setEditingCustom] = useState(null);
  const [discountFor, setDiscountFor] = useState(null);

  const unnecessary = (managing?.plan?.lines || []).flatMap(
    (l) => l.unnecessary_production_ids || []
  );

  const load = useCallback(() => {
    setError("");
    api
      .get("/orders")
      .then(({ data }) => setOrders(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  useEffect(load, [load]);

  // "Fazer novo pedido" na ficha do cliente: abre o fluxo oficial já com o cadastro.
  useEffect(() => {
    const target = location.state?.newOrderCustomer;
    if (!target) return;
    setOrderCustomer(target);
    setCreating(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const changeStatus = async (order, status) => {
    await apply(order, status);
  };

  const confirmPayment = async (order) => {
    try {
      await api.put(`/orders/${order.id}/payment`, { payment_status: "CONFIRMADO" });
      toast.success("Pagamento confirmado. Estoque baixado e receita lançada.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConfirming(null);
    }
  };

  const apply = async (order, status) => {
    try {
      await api.put(`/orders/${order.id}/status`, { status });
      toast.success(
        status === "CANCELADO" ? "Pedido cancelado." : `Pedido atualizado: ${STATUS[status].label}.`
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConfirming(null);
    }
  };

  const chargePix = async (order) => {
    try {
      const { data } = await api.post(`/orders/${order.id}/pix-charge`);
      setPixFor({ ...data, order });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openProductions = async (order) => {
    setManaging(order);
    setProds(null);
    try {
      const { data } = await api.get("/productions", { params: { order_id: order.id } });
      setProds(data);
    } catch (e) {
      toast.error(apiError(e));
      setProds([]);
    }
  };

  const removeProduction = async (p) => {
    try {
      await api.delete(`/productions/${p.id}`);
      toast.success("Produção desfeita. O estado do pedido foi recalculado.");
      await openProductions(managing);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUndoing(null);
    }
  };

  const shareTracking = async (order, mode) => {
    try {
      const { data } = await api.get(`/orders/${order.id}/tracking`);
      const link = `${window.location.origin}${data.path}`;
      if (mode === "copy") {
        await navigator.clipboard.writeText(link);
        toast.success("Link de acompanhamento copiado.");
        return;
      }
      const text = data.message_template.replace("{link}", link);
      const base = data.phone ? `https://wa.me/${data.phone}` : "https://wa.me/";
      window.open(`${base}?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (orders === null) return <Loading label="Carregando pedidos..." />;

  const byKind = (o) =>
    kind === "TODOS"
      ? true
      : kind === "REPOSICAO"
      ? o.origin === "REPOSICAO_PARCEIRO"
      : o.origin !== "REPOSICAO_PARCEIRO";

  const q = norm(search);
  const countOf = (statuses) =>
    orders.filter((o) => (statuses ? statuses.includes(o.status) : true)).length;

  const activeStatuses = TABS.find(([k]) => k === tab)?.[2] || null;
  const visible = orders
    .filter((o) => (activeStatuses ? activeStatuses.includes(o.status) : true))
    .filter(byKind)
    .filter((o) => (q ? matchesSearch(o, q) : true))
    .sort((a, b) => {
      if (tab === "A_FAZER") {
        const prio = (o) => (o.status === "PAGO" ? 0 : 1);
        if (prio(a) !== prio(b)) return prio(a) - prio(b);
        return new Date(a.created_at) - new Date(b.created_at);
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div className="space-y-6" data-testid="orders-page">
      <PageHeader
        title="Pedidos"
        subtitle="Pedidos do catálogo e pedidos combinados no WhatsApp. Produção e pagamento são independentes: o estoque só é baixado quando você confirma o pagamento."
        action={
          <Button
            onClick={() => setCreating(true)}
            data-testid="new-order-btn"
            className="h-10 gap-1.5 rounded-full font-semibold"
          >
            <Plus className="h-4 w-4" /> Novo pedido
          </Button>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {TABS.map(([key, label, statuses]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`orders-tab-${key.toLowerCase()}`}
              className={`tr-chip ${tab === key ? "tr-chip-active" : ""}`}
            >
              {label} <span className="ml-1 font-num opacity-60">{countOf(statuses)}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex gap-2">
            {[["TODOS", "Todos"], ["VENDA", "Venda"], ["REPOSICAO", "Reposição"]].map(
              ([key, label]) => (
                <button
                  key={key}
                  onClick={() => setKind(key)}
                  data-testid={`orders-kind-${key.toLowerCase()}`}
                  className={`tr-chip ${kind === key ? "tr-chip-active" : ""}`}
                >
                  {label}
                </button>
              )
            )}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cliente, WhatsApp, pedido, produto..."
              className="pl-9"
              data-testid="orders-search-input"
            />
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={ReceiptText}
          title={orders.length === 0 ? "Nenhum pedido recebido" : "Nada nesta aba"}
          description={
            orders.length === 0
              ? "Compartilhe o link do seu catálogo público para começar a receber pedidos."
              : "Troque de aba ou limpe a busca para ver outros pedidos."
          }
        />
      ) : (
        <div className="grid gap-4 stagger lg:grid-cols-2 [&>*]:min-w-0">
          {visible.map((o) => (
            <Surface key={o.id} className="p-5" data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    {o.origin === "REPOSICAO_PARCEIRO" ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-300"
                        data-testid={`order-consignment-badge-${o.id}`}
                      >
                        <Handshake className="h-3 w-3" /> Reposição {o.number}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300"
                        data-testid={`order-sale-badge-${o.id}`}
                      >
                        <ShoppingCart className="h-3 w-3" /> Venda {o.number || ""}
                      </span>
                    )}
                  </div>
                  <p className="truncate font-display text-base font-medium" data-testid={`order-customer-${o.id}`}>
                    {o.customer_name}
                  </p>
                  <p className="font-num text-xs text-muted-foreground">{o.phone}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{dataHora(o.created_at)}</p>
                  {o.receiving?.method && (
                    <div className="mt-2 rounded-lg border border-white/10 bg-secondary/40 p-2.5 text-[11px]"
                         data-testid={`order-receiving-${o.id}`}>
                      {o.receiving.method === "RETIRADA" ? (
                        <>
                          <span className="font-semibold text-brand">Retirada</span>
                          <span className="text-muted-foreground"> · {o.receiving.city}</span>
                          <p className="mt-1 text-muted-foreground">
                            Confirmar o horário da retirada com o cliente pelo WhatsApp.
                          </p>
                        </>
                      ) : (
                        <>
                          <span className="font-semibold text-brand">
                            Entrega · {o.receiving.shipping_preference === "MOTOBOY"
                              ? "Motoboy" : "Correios"}
                          </span>
                          <p className="mt-1 text-muted-foreground">
                            {o.receiving.street}, {o.receiving.number}
                            {o.receiving.complement ? ` · ${o.receiving.complement}` : ""}
                            <br />
                            {o.receiving.district} · {o.receiving.city} · CEP {o.receiving.cep}
                            {o.receiving.reference
                              ? <><br />Referência: {o.receiving.reference}</> : null}
                            {o.receiving.notes
                              ? <><br />Obs.: {o.receiving.notes}</> : null}
                          </p>
                          <p className="mt-1 text-amber-300">
                            Preferência do cliente · custo da entrega ainda a confirmar.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {o.origin !== "REPOSICAO_PARCEIRO" && (
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS[o.status]?.cls}`}
                      data-testid={`order-production-status-${o.id}`}
                    >
                      <Factory className="mr-1 inline h-3 w-3" />
                      {o.plan?.label || STATUS[o.status]?.label || o.status}
                    </span>
                    <span
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                        o.payment_status === "CONFIRMADO"
                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                          : "border-amber-500/40 bg-amber-500/15 text-amber-300"
                      }`}
                      data-testid={`order-payment-status-${o.id}`}
                    >
                      <Wallet className="mr-1 inline h-3 w-3" />
                      {o.payment_status === "CONFIRMADO" ? "Pagamento confirmado" : "Pagamento aguardando"}
                      {o.payment_status !== "CONFIRMADO" && o.payment_method
                        ? ` · ${o.payment_method}`
                        : ""}
                    </span>
                  </div>
                )}
                {o.origin === "REPOSICAO_PARCEIRO" && (
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS[o.status]?.cls}`}
                    data-testid={`order-status-${o.id}`}
                  >
                    {STATUS[o.status]?.label || o.status}
                  </span>
                )}
              </div>

              <ul className="mt-4 space-y-1.5 text-sm">
                {o.items.map((it, idx) => (
                  <li key={`${it.product_id || it.kit_id || "item"}-${it.variation_id || ""}-${idx}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate">
                        <span className="font-num">{it.quantity}x</span> {it.name}
                        {it.kind === "kit" && (
                          <span className="ml-1.5 rounded-full border border-primary/40 bg-primary/15 px-1.5 py-0.5 text-[10px] uppercase text-brand">
                            Kit
                          </span>
                        )}
                      </span>
                      <span className="font-num text-xs text-muted-foreground">
                        {brl(it.unit_price * it.quantity)}
                      </span>
                    </div>
                    {it.kind === "kit" && it.components?.length > 0 && (
                      <p className="pl-4 text-xs text-muted-foreground">
                        {it.components.map((c) => `${c.quantity * it.quantity}x ${c.name}`).join(" + ")}
                      </p>
                    )}
                    {it.customization?.length > 0 && (
                      <div
                        className="mt-1.5 rounded-lg border border-brand/25 bg-brand/5 p-2.5"
                        data-testid={`order-customization-${o.id}-${idx}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-brand">
                            Personalização
                          </p>
                          {o.customization_status && o.status !== "CANCELADO" && (
                            <button
                              onClick={() => setEditingCustom({ order: o, index: idx, item: it })}
                              data-testid={`edit-customization-btn-${o.id}-${idx}`}
                              className="text-[10px] text-muted-foreground underline transition-colors hover:text-foreground"
                            >
                              Editar
                            </button>
                          )}
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {it.customization.map((c) => (
                            <li key={c.name} className="text-xs text-muted-foreground">
                              {c.name}: <span className="text-foreground">{c.value}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {o.customization_status && (
                <p
                  className={`mt-3 rounded-lg border p-2.5 text-xs ${
                    o.customization_status === "APROVADA"
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : "border-amber-500/40 bg-amber-500/10 text-amber-200"
                  }`}
                  data-testid={`order-customization-status-${o.id}`}
                >
                  {o.customization_status === "APROVADA"
                    ? `Personalização aprovada pelo cliente${
                        o.customization_approved_at ? ` em ${dataBR(o.customization_approved_at)}` : ""
                      }`
                    : `${o.customization_label} — envie o link do pedido para o cliente aprovar antes de produzir.`}
                </p>
              )}
              {o.plan?.lines?.length > 0 && o.origin !== "REPOSICAO_PARCEIRO" && (
                <div className="mt-4 space-y-2 rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid={`order-plan-${o.id}`}>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    O que falta para entregar
                  </p>
                  {o.plan.lines.map((l) => (
                    <div key={l.key} data-testid={`order-plan-line-${o.id}-${l.key}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate text-sm">
                          <span className="font-num">{l.quantity}x</span> {l.name}
                        </span>
                        <span
                          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${LINE_STATE[l.state]}`}
                          data-testid={`order-plan-state-${o.id}-${l.key}`}
                        >
                          {l.state_label}
                        </span>
                      </div>
                      <p className="font-num text-[11px] text-muted-foreground">
                        Estoque: {l.in_stock}
                        {l.already_applied > 0 ? ` · Já baixado: ${l.already_applied}` : ""}
                        {l.to_produce > 0 ? ` · A produzir: ${l.to_produce}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {(o.payment_method || o.delivery) && o.origin !== "REPOSICAO_PARCEIRO" && (
                <p className="mt-3 text-xs text-muted-foreground" data-testid={`order-payment-info-${o.id}`}>
                  {[o.payment_method, o.delivery].filter(Boolean).join(" · ")}
                  {o.source === "MANUAL" ? " · pedido manual" : ""}
                </p>
              )}
              {o.notes && <p className="mt-3 text-xs text-muted-foreground">Obs.: {o.notes}</p>}

              <div className="mt-4 flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 shrink-0">
                  {o.discount_amount > 0 && (
                    <div className="mb-1 space-y-0.5 text-xs" data-testid={`order-discount-${o.id}`}>
                      <p className="text-muted-foreground">
                        Subtotal: <span className="font-num">{brl(o.subtotal_original)}</span>
                      </p>
                      <p className="text-amber-300">
                        Desconto:{" "}
                        <span className="font-num">
                          {o.discount_type === "PERCENTUAL"
                            ? `${o.discount_value}% (- ${brl(o.discount_amount)})`
                            : `- ${brl(o.discount_amount)}`}
                        </span>
                      </p>
                    </div>
                  )}
                  <span className="font-num text-lg text-brand" data-testid={`order-total-${o.id}`}>
                    {brl(o.total)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPdf(`/orders/${o.id}/receipt-pdf`)}
                    data-testid={`order-receipt-pdf-btn-${o.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs"
                  >
                    <FileText className="h-3.5 w-3.5" /> Comprovante (novo)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPdf(`/orders/${o.id}/pdf`)}
                    data-testid={`order-pdf-btn-${o.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs"
                  >
                    <FileText className="h-3.5 w-3.5" /> Gerar PDF
                  </Button>
                  {o.origin === "REPOSICAO_PARCEIRO" ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid={`order-consignment-note-${o.id}`}
                    >
                      Consignação — acompanhe e mude o status em Parceiros → Reposições
                    </p>
                  ) : (
                    <>
                  {o.payment_status !== "CONFIRMADO" && o.status !== "CANCELADO" && (
                    <Button
                      size="sm"
                      onClick={() => setConfirming({ order: o })}
                      data-testid={`confirm-payment-btn-${o.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Confirmar pagamento
                    </Button>
                  )}
                  {o.payment_status !== "CONFIRMADO" && o.status !== "CANCELADO" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDiscountFor(o)}
                      data-testid={`order-discount-btn-${o.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs"
                    >
                      <Percent className="h-3.5 w-3.5" />
                      {o.discount_amount > 0 ? "Editar desconto" : "Desconto"}
                    </Button>
                  )}
                  {o.payment_status !== "CONFIRMADO" && o.status !== "CANCELADO" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => chargePix(o)}
                      data-testid={`charge-pix-btn-${o.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs"
                    >
                      <QrCode className="h-3.5 w-3.5" /> Cobrar Pix
                      {o.pix_charge?.declared_at ? " · cliente avisou" : ""}
                    </Button>
                  )}
                  <Select
                    value={o.status === "PAGO" ? "PENDENTE" : o.status}
                    disabled={o.status === "CANCELADO"}
                    onValueChange={(v) => v !== o.status && changeStatus(o, v)}
                  >
                    <SelectTrigger className="h-9 w-[170px] shrink-0 text-xs" data-testid={`order-status-select-${o.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-popover">
                      {PRODUCTION_STATUS.map((k) => (
                        <SelectItem key={k} value={k} className="text-xs">
                          {STATUS[k].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => shareTracking(o, "copy")}
                    data-testid={`copy-tracking-btn-${o.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs"
                  >
                    <Link2 className="h-3.5 w-3.5" /> Link do cliente
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => shareTracking(o, "whatsapp")}
                    data-testid={`send-tracking-btn-${o.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs"
                  >
                    <Send className="h-3.5 w-3.5" /> Enviar no WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openProductions(o)}
                    data-testid={`manage-production-btn-${o.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs"
                  >
                    <Factory className="h-3.5 w-3.5" /> Gerenciar produção
                  </Button>
                    </>
                  )}
                </div>
              </div>
              {situation(o) && (
                <p
                  className={`mt-2 text-xs ${TONE[situation(o).tone]}`}
                  data-testid={`order-situation-${o.id}`}
                >
                  {situation(o).text}
                </p>
              )}
            </Surface>
          ))}
        </div>
      )}

      {creating && (
        <NewOrderModal
          customer={orderCustomer}
          onClose={() => {
            setCreating(false);
            setOrderCustomer(null);
          }}
          onCreated={(order) => {
            setCreating(false);
            setOrderCustomer(null);
            setCreated(order);
            load();
          }}
        />
      )}
      {editingCustom && (
        <EditCustomizationModal
          order={editingCustom.order}
          index={editingCustom.index}
          item={editingCustom.item}
          onClose={() => setEditingCustom(null)}
          onSaved={() => {
            setEditingCustom(null);
            load();
          }}
        />
      )}
      {discountFor && (
        <DiscountModal
          order={discountFor}
          onClose={() => setDiscountFor(null)}
          onSaved={() => {
            setDiscountFor(null);
            load();
          }}
        />
      )}
      {pixFor && (
        <PixChargeModal
          charge={pixFor}
          onClose={() => {
            setPixFor(null);
            load();
          }}
        />
      )}
      <AlertDialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="order-created-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pedido #{created?.number} criado</AlertDialogTitle>
            <AlertDialogDescription>
              O link abaixo é só para o cliente acompanhar o andamento — ele não precisa comprar de
              novo.
            </AlertDialogDescription>
          </AlertDialogHeader>
        <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => created && shareTracking(created, "copy")}
              data-testid="created-copy-link-btn"
              className="h-10 gap-1.5 rounded-full text-xs"
            >
              <Link2 className="h-3.5 w-3.5" /> Copiar link
            </Button>
            <Button
              onClick={() => created && shareTracking(created, "whatsapp")}
              data-testid="created-whatsapp-btn"
              className="h-10 gap-1.5 rounded-full text-xs font-semibold"
            >
              <Send className="h-3.5 w-3.5" /> Enviar no WhatsApp
            </Button>
            {created?.payment_status !== "CONFIRMADO" && (
              <Button
                variant="outline"
                onClick={() => {
                  const order = created;
                  setCreated(null);
                  chargePix(order);
                }}
                data-testid="created-charge-pix-btn"
                className="h-10 gap-1.5 rounded-full text-xs"
              >
                <QrCode className="h-3.5 w-3.5" /> Cobrar Pix
              </Button>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="created-close-btn">Abrir pedido</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="confirm-payment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar o pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              A venda de <strong>{confirming?.order.customer_name}</strong> será registrada e as
              quantidades serão baixadas do estoque de produtos. Essa baixa acontece uma única vez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-payment-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmPayment(confirming.order)}
              data-testid="confirm-payment-dialog-btn"
            >
              Confirmar e baixar estoque
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!managing} onOpenChange={(o) => !o && setManaging(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="manage-production-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Produção deste pedido</AlertDialogTitle>
            <AlertDialogDescription>
              Desfazer uma produção não apaga o pedido, os itens, o histórico nem o financeiro. O
              estado do pedido é recalculado automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            {prods === null && <p className="text-sm text-muted-foreground">Carregando...</p>}
            {prods?.length === 0 && (
              <div data-testid="no-productions-for-order">
                <p className="text-sm">Este pedido não possui produção pendente.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Os itens necessários estão disponíveis em estoque ou não precisam ser produzidos.
                </p>
              </div>
            )}
            {managing?.plan?.lines?.length > 0 && (
              <div className="space-y-1 rounded-lg border border-white/10 bg-secondary/30 p-3">
                {managing.plan.lines.map((l) => (
                  <div key={l.key} className="flex items-center justify-between gap-3 text-xs">
                    <span className="min-w-0 truncate">
                      <span className="font-num">{l.quantity}x</span> {l.name}
                    </span>
                    <span className="shrink-0 font-num text-muted-foreground">
                      estoque {l.in_stock}
                      {l.to_produce > 0 ? ` · produzir ${l.to_produce}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {prods?.map((p) => (
              <div
                key={p.id}
                className="rounded-lg border border-white/10 bg-secondary/40 p-3"
                data-testid={`order-production-${p.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm">
                    {p.product_name}
                    {p.variation_name ? ` · ${p.variation_name}` : ""}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">{p.status}</span>
                </div>
                <p className="mt-1 font-num text-[11px] text-muted-foreground">
                  {p.quantity_needed} un · {p.plate_name || "sem Plate"}
                </p>
                {p.can_delete ? (
                  <>
                    {unnecessary.includes(p.id) && (
                      <p className="mt-2 text-[11px] text-emerald-300">
                        Já existe estoque para este item — esta produção pode ser desfeita.
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUndoing(p)}
                      data-testid={`delete-order-production-btn-${p.id}`}
                      className="mt-2 h-8 gap-1.5 rounded-full text-[11px] text-red-300"
                    >
                      <Trash2 className="h-3 w-3" /> Desfazer produção
                    </Button>
                  </>
                ) : (
                  <p className="mt-2 text-[11px] text-amber-300">
                    Esta produção já possui movimentações e não pode ser desfeita dessa forma.
                    Utilize o fluxo de cancelamento/estorno existente.
                  </p>
                )}
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="close-manage-production-btn">
              Voltar ao pedido
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!undoing} onOpenChange={(o) => !o && setUndoing(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="undo-production-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer esta produção?</AlertDialogTitle>
            <AlertDialogDescription>
              A produção será removida e o estado do pedido recalculado. O pedido, os itens, o
              histórico, o financeiro e o estoque não são alterados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-undo-production-btn">Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeProduction(undoing)}
              data-testid="confirm-undo-production-btn"
            >
              Desfazer produção
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
